import React, { useEffect, useState } from "react"
import { ReactComponent as Details } from "assets/customIcons/details.svg"
import { ReactComponent as Files } from "assets/customIcons/files.svg"
import { ReactComponent as Layers } from "assets/customIcons/layers.svg"
import { ReactComponent as Certificates } from "assets/customIcons/certificates.svg"
import PropTypes from "prop-types"
import i18n from "i18n"
import { v4 as uuidv4 } from "uuid"
import PartDetails from "./detallesPartida/partDetails"
import { useBudget2023Context } from "context/budget2023/Budget2023Context"
import { Stack, CircularProgress } from "@mui/material"
import validateFields from "./validateFields"
import FileIndex from "./fileHandler/fileIndex"
import { useBankPricesContext } from "context/bankPrices/bankPricesContext"
import PriceSearcher from "./priceSearch"
import {
  calcularImporteDeDescomposicion,
  getCurrency,
  partidaGetClientPrice,
} from "utils/functions"
import { useAuthContext } from "context/auth/authContext"
import { isNil } from "lodash"
import { API, Auth } from "aws-amplify"
import DecompositionTypeEnum from "common/DecompositionTypeEnum"
import { calcCostTotal, calcPartida, calcApplyDiscount } from "utils/partUtils"
import Tooltip from "@mui/material/Tooltip"
import { useLocation } from "react-router-dom/cjs/react-router-dom"
import { useWrapperModals } from "context/wrapperModals/WrapperModalsContext"
import { useModalPartida } from "hooks/modalPartida/useModalPartida"
import RightFooterGrid from "./RightFooterGrid"

const decompositionTypeMap = {
  0: "coste",
  1: "manodeobra",
  2: "maquinaria",
  3: "material",
  4: "residuoAdicional",
  5: "residuoClasificacion",
}

const RightGridContent = ({
  bancoDePrecios,
  partidaState,
  details,
  setDetails,
  handleClick,
  setErrorMessage,
  setPartidaState,
  imInUpdate,
  setOpen,
  referenciaObject,
  srcPage,
}) => {
  const location = useLocation()
  const isBankPricePath = location.pathname === "/bancodeprecios"

  const contents =
    bancoDePrecios == "BANCO DE PRECIOS REFERENCIA"
      ? ["Details"]
      : ["Details", "Files", "Layers", "Certificates", "PartSearcher"]
  const [content, setContent] = useState("Details")
  const {
    createPartidaV2,
    updatePartidav2,
    isContradictingPart,
    setIsContradictingPart,
    isBudgetClosed,
    marginModal,
  } = useBudget2023Context()
  const [loading, setLoading] = useState(false)
  const { createPartBankTree, updatePartPrice, getPartsLenghtByChapterId } =
    useBankPricesContext()
  const { user } = useAuthContext()
  const { newModalPart, bc3Link } = useWrapperModals()

  const { state, loadInitialRightFooterData } = useModalPartida()

  useEffect(() => {
    loadInitialRightFooterData(partidaState, marginModal.formula)
  }, [])

  const SERVICE_CORE_BASE = "service-core-base"

  const transformTypeToNumber = type => {
    return (
      Object.keys(decompositionTypeMap).find(
        key => decompositionTypeMap[key] === type,
      ) || 3
    )
  }

  const handleContradictingPart = async () => {
    try {
      setIsContradictingPart(false)
      const { partidaId, ...partidaStateForBody } = partidaState
      partidaStateForBody.decomposition = details.map(item => {
        return {
          ...item,
          price: parseFloat(item.price),
          quantity: parseFloat(item.quantity),
          type: item.type
            ? DecompositionTypeEnum[item.type]
            : DecompositionTypeEnum.material,
        }
      })
      const newBody = {
        ...partidaStateForBody,
        quantity: parseFloat(state.leftGridContent.quantity),
        margin: parseFloat(state.rightFooter.marginPercent),
        price: parseFloat(state.leftGridContent.cost),
        clientPriceWithoutDiscount: parseFloat(state.rightFooter.unitPrice),
      }

      let payload = {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
          "Access-Control-Allow-Origin": "*",
        },
        body: newBody,
      }
      const resp = await API.post(
        SERVICE_CORE_BASE,
        `/extras/contradict/${partidaId}`,
        payload,
      )
      setOpen(false)
      window.location.href = `/extras/${partidaState.projectId}`
    } catch (error) {
      console.log("🚀 error, newModalPart", error)
      setOpen(false)
    }
  }

  const handleCreatePartida = async () => {
    setLoading(true)
    if (validateFields(partidaState, referenciaObject, setErrorMessage)) {
      if (!bancoDePrecios) {
        await createPartidaV2(
          partidaState.accountId,
          partidaState.chapterId,
          partidaState.projectId,
          "last",
          undefined,
          {
            ...partidaState,
            decomposition: details,
            quantity: parseFloat(state.leftGridContent.quantity),
            margin: parseFloat(state.rightFooter.marginPercent),
            price: parseFloat(state.leftGridContent.cost),
            priceReduced: parseFloat(state.leftGridContent.priceReduced),
            costReduction: parseFloat(state.leftGridContent.costReduction),
            clientPrice: parseFloat(state.rightFooter.unitPriceWithDiscount),
            clientPriceWithoutDiscount: parseFloat(state.rightFooter.unitPrice),
            discount: parseFloat(state.rightFooter.discountPercentage),
          },
          newModalPart.from,
        )
      } else {
        const position = await getPartsLenghtByChapterId(partidaState.chapterId)
        await createPartBankTree({
          ...partidaState,
          decomposition: details,
          chapterBankId: partidaState.chapterId,
          userPrice: parseFloat(state.leftGridContent.cost),
          quantity: parseFloat(state.leftGridContent.quantity),
          price: parseFloat(state.leftGridContent.cost),
          code: uuidv4(),
          position: position,
        })
        setDetails([])
      }
      setLoading(false)
      setOpen(false)
    } else {
      handleClick()
      setLoading(false)
    }
    bc3Link.setProperty({
      open: false,
      data: null,
      chapterId: null,
    })
  }

  const handleUpdatePartida = async () => {
    setLoading(true)
    if (!bancoDePrecios) {
      await updatePartidav2(
        {
          ...partidaState,
          decomposition: details,
          costeIndirecto: 0,
          industrialProfit: 0,
          quantity: parseFloat(state.leftGridContent.quantity),
          margin: parseFloat(state.rightFooter.marginPercent),
          price: parseFloat(state.leftGridContent.cost),
          discount: state.rightFooter.discountPercentage,
          priceReduced: parseFloat(state.leftGridContent.priceReduced),
          costReduction: parseFloat(state.leftGridContent.costReduction),
          clientPriceWithoutDiscount: parseFloat(state.rightFooter.unitPrice),
          clientPrice: state.rightFooter.unitPriceWithDiscount,
        },
        partidaState.chapterId,
        partidaState.accountId,
        false,
        newModalPart.from,
      )
    } else {
      await updatePartPrice(
        {
          ...partidaState,
          decomposition: details,
          costeIndirecto: 0,
          industrialProfit: 0,
          userPrice: parseFloat(state.leftGridContent.cost),
          quantity: parseFloat(state.leftGridContent.quantity),
          price: parseFloat(state.leftGridContent.cost),
          code: uuidv4(),
          position: 0,
          clientPrice: calcApplyDiscount(
            parseFloat(state.rightFooter.unitPrice),
            partidaState.discount,
          ),
        },
        partidaState.chapterId,
        partidaState.accountId,
        false,
      )
    }

    console.log("partidaState", partidaState)

    setLoading(false)
    setOpen(false)
    bc3Link.setProperty({
      open: false,
      data: null,
      chapterId: null,
    })
    setDetails([])
  }

  const currencySymbol = getCurrency(user["custom:currency"])

  const handleChange = e => {
    const { name, value } = e.target
    console.log(`handleChange: ${name} => ${value}`)
    switch (name) {
      case "clientPrice":
        // Calculate margin based on client price
        let clientPrice = parseFloat(value)
        let cost = partidaState.price * partidaState.quantity
        let margin = 100 * (1 - cost / clientPrice)
        console.log(`margin => ${margin}`)
        setPartidaState({
          ...partidaState,
          margin: cost ? margin : 0,
          clientPrice: clientPrice,
        })
        break
    }
  }

  const handleViewChange = e => {
    const { name, value } = e.target
    console.log(`handleViewChange: ${name} => ${value}`)

    // TODO: Use partidaViewState instead of partidaState ?
    setPartidaState({
      ...partidaState,
      [name]: value,
    })
  }

  const handleViewBlur = e => {
    var { name, value } = e.target
    console.log(`handleViewBlur ${name} => ${value}`)

    switch (name) {
      case "marginPercView":
        if (value < 0) {
          value = 0
        }
        if (value >= 100) {
          value = 99.99
        }

        partidaState = calcPartida(
          partidaState,
          "margin",
          parseFloat(value),
          marginModal.formula,
        )
        break
      case "priceView":
        partidaState = calcPartida(
          partidaState,
          "clientPrice",
          parseFloat(value),
          marginModal.formula,
        )
        break
      case "marginPriceView":
        // Pf = Pc / (1 - m/100)
        // Pf = Pc + Mp
        var priceTotal = parseFloat(value) + calcCostTotal(partidaState)
        var clientPrice =
          priceTotal /
          parseFloat(partidaState.quantity ? partidaState.quantity : 1)
        partidaState = calcPartida(
          partidaState,
          "clientPrice",
          clientPrice,
          marginModal.formula,
        )
        break
      case "priceTotalView":
        var price =
          parseFloat(value) /
          parseFloat(partidaState.quantity ? partidaState.quantity : 1)
        partidaState = calcPartida(
          partidaState,
          "clientPrice",
          price,
          marginModal.formula,
        )
        break
    }

    // If name ends with 'View' and value is empty, remove the key from the state
    // So view gets reset to the calculated value
    if (name.endsWith("View")) {
      console.log(`Removing ${name} from state`)
      setPartidaState(_.omit(partidaState, name))
      return
    } else {
      setPartidaState({
        ...partidaState,
        [name]: value,
      })
    }

    // Format to 2 decimal places
    //    setPartidaState({
    //      ...partidaState,
    //      [name]: parseFloat(value).toFixed(2)
    //    })
  }

  const [creatingDetailInViewMode, setCreatingDetailInViewMode] =
    useState(false)

  useEffect(() => {
    if (imInUpdate || !isNil(bc3Link.data)) {
      if (partidaState.decomposition) {
        let aux = partidaState.decomposition
        for (let i = 0; i < partidaState.decomposition.length; i++) {
          const totalAux = parseFloat(
            calcularImporteDeDescomposicion(partidaState.decomposition[i]),
          ).toFixed(2)
          aux[i].total = totalAux

          aux[i].price = partidaState.decomposition[i].price
          aux[i].quantity = partidaState.decomposition[i].quantity
          aux[i].unity = partidaState.decomposition[i].unity.toString()
          aux[i].description =
            partidaState.decomposition[i].description.toString()

          let typeEnumStr
          switch (partidaState.decomposition[i].type) {
            case 0:
              typeEnumStr = "coste"
              break
            case 1:
              typeEnumStr = "manodeobra"
              break
            case 2:
              typeEnumStr = "maquinaria"
              break
            case 3:
              typeEnumStr = "material"
              break
            case 4:
              typeEnumStr = "residuoAdicional"
              break
            case 5:
              typeEnumStr = "residuoClasificacion"
              break
          }

          aux[i].type = typeEnumStr
            ? typeEnumStr
            : partidaState.decomposition[i].type
        }
        setDetails(aux)
        setCreatingDetailInViewMode(false)
      }
    } else {
      setCreatingDetailInViewMode(false)
    }
  }, [partidaState.decomposition])

  const getSvg = cont => {
    if (cont == "Details") {
      return (
        <>
          <Details fillOpacity={cont == content ? 1 : 0.5} />
          <a style={{ opacity: cont == content ? 1 : 0.5 }}>Detalle partida</a>
        </>
      )
    } else if (cont == "Files") {
      return (
        <>
          <Files fillOpacity={cont == content ? 1 : 0.5} />
          <a style={{ opacity: cont == content ? 1 : 0.5 }}>
            Archivos adjuntos
          </a>
        </>
      )
    } else if (cont == "Layers") {
      return (
        <>
          <Layers fillOpacity={cont == content ? 1 : 0.5} />
          <a style={{ opacity: cont == content ? 1 : 0.5 }}>Pliegos</a>
        </>
      )
    } else if (cont == "Certificates") {
      return (
        <>
          <Certificates fillOpacity={cont == content ? 1 : 0.5} />
          <a style={{ opacity: cont == content ? 1 : 0.5 }}>Certificados</a>
        </>
      )
    }
    if (cont == "PartSearcher") {
      return (
        <>
          <Details fillOpacity={cont == content ? 1 : 0.5} />
          <a style={{ opacity: cont == content ? 1 : 0.5 }}>Buscar precio</a>
        </>
      )
    }
  }

  return (
    <Stack direction="column" sx={{ height: "100%" }}>
      <div
        style={{
          width: "100%",
          height: "61px",
          backgroundColor: "#D9D9D9",
          borderTopRightRadius: "10px",
          display: "flex",
          alignItems: "center",
          paddingLeft: "16px",
          paddingRight: "20px",
        }}
      >
        <ul
          style={{
            display: "flex",
            listStyle: "none",
            marginBottom: "0",
            gap: "18px",
            padding: "0",
            height: "100%",
          }}
        >
          {contents.map(
            (c, index) =>
              c !== "Layers" &&
              c !== "Certificates" &&
              (imInUpdate || c !== "PartSearcher") && (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    gap: "6px",
                    cursor: "pointer",
                    height: "92%",
                    borderBottom: c == content ? "2px solid #414141" : "none",
                    alignItems: "center",
                  }}
                  onClick={() => {
                    setContent(c)
                  }}
                >
                  {getSvg(c)}
                </li>
              ),
          )}
        </ul>
        {loading ? (
          <CircularProgress
            size={24}
            style={{ marginLeft: "auto", marginRight: "0px" }}
          />
        ) : bancoDePrecios == "BANCO DE PRECIOS REFERENCIA" ? null : (
          <Tooltip
            title={
              isBankPricePath
                ? ""
                : isBudgetClosed &&
                    !isContradictingPart &&
                    srcPage != "extras" &&
                    srcPage != "priceBanks"
                  ? i18n.t("validation.editNotAllowedProjectClosed")
                  : Number(partidaState?.cert_previous ?? 0) > 0
                    ? i18n.t("validation.certifiedItem")
                    : ""
            }
          >
            <button
              disabled={
                (!isBankPricePath &&
                  isBudgetClosed &&
                  !isContradictingPart &&
                  srcPage !== "extras" &&
                  srcPage !== "priceBanks") ||
                Number(partidaState?.cert_previous ?? 0) > 0
              }
              onClick={() => {
                isContradictingPart
                  ? handleContradictingPart()
                  : imInUpdate
                    ? handleUpdatePartida()
                    : handleCreatePartida()
              }}
              style={{
                marginLeft: "auto",
                marginRight: "0px",
                width: "102px",
                height: "38px",
                backgroundColor:
                  (!isBankPricePath &&
                    isBudgetClosed &&
                    !isContradictingPart &&
                    srcPage != "extras" &&
                    srcPage != "priceBanks") ||
                  Number(partidaState?.cert_previous ?? 0) > 0
                    ? "#FFFFFF"
                    : "#FFD52B",
              }}
              className="AddContact"
            >
              {i18n.t("Guardar")}
            </button>
          </Tooltip>
        )}
      </div>
      <div style={{ height: "calc(100% - 187.5px)", overflowY: "auto" }}>
        {content == "Details" ? (
          <PartDetails
            bancoDePrecios={bancoDePrecios}
            partidaState={partidaState}
            creatingDetailInViewMode={creatingDetailInViewMode}
            setCreatingDetailInViewMode={setCreatingDetailInViewMode}
            details={details}
            setDetails={setDetails}
            showDynamicElementButton
          />
        ) : content == "Files" ? (
          <FileIndex
            imInUpdate={imInUpdate}
            partidaState={partidaState}
            setPartidaState={setPartidaState}
          />
        ) : content == "PartSearcher" && imInUpdate ? (
          <PriceSearcher
            partidaState={partidaState}
            setPartidaState={setPartidaState}
            setDetails={setDetails}
            details={details}
            setContent={setContent}
          />
        ) : null}
      </div>

      <RightFooterGrid
        currency={currencySymbol}
        bancoDePrecios={bancoDePrecios}
      />
    </Stack>
  )
}

RightGridContent.propTypes = {
  partidaState: PropTypes.object,
  setPartidaState: PropTypes.func,
  imInUpdate: PropTypes.bool,
  setOpen: PropTypes.func,
  referenciaObject: PropTypes.object,
  handleClick: PropTypes.func,
  setErrorMessage: PropTypes.func,
  details: PropTypes.array,
  setDetails: PropTypes.func,
  bancoDePrecios: PropTypes.string,
  srcPage: PropTypes.string,
}

export default RightGridContent
